import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleAuthButton, AppleAuthButton } from '@/components/auth/OAuthButtons';
import {
  TextField,
  PasswordField,
  OrDivider,
  FormError,
  SubmitButton,
} from '@/components/auth/FormFields';
import { login, readApiError } from '@/lib/auth-api';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/app';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ email, password });
      navigate(next, { replace: true });
    } catch (err) {
      setError(readApiError(err, t('auth.error_invalid_credentials')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.login_title')}
      subtitle={t('auth.login_subtitle')}
      footer={
        <>
          {t('auth.no_account')}{' '}
          <Link to="/signup" className="font-semibold text-accent-700 hover:underline">
            {t('auth.signup_link')}
          </Link>
        </>
      }
    >
      <div className="space-y-3">
        <GoogleAuthButton className="w-full" />
        <AppleAuthButton className="w-full" />
      </div>

      <div className="my-6">
        <OrDivider label={t('auth.or_continue_with_email')} />
      </div>

      <form className="space-y-4" onSubmit={submit} noValidate>
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leadingIcon={<Mail className="h-4 w-4" />}
        />
        <PasswordField
          label={t('auth.password')}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs font-medium text-accent-700 hover:underline">
            {t('auth.forgot_password_link')}
          </Link>
        </div>

        <FormError message={error} />

        <SubmitButton loading={loading}>{t('auth.login_submit')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
